import type { SessionUser } from '@tandemone/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'

import { api } from '@/api/client'
import { disconnectSocket } from '@/api/socket'
import { clearUser, identify, reset, setUser } from '@/domains/observability'

import { authClient } from './auth-client'

export const ME_KEY = ['me'] as const
export const ME_URL = '/v1/me'

async function fetchMe(signal?: AbortSignal): Promise<SessionUser | null> {
  try {
    const { data } = await api.get<SessionUser>(ME_URL, { signal })
    return data
  } catch {
    return null
  }
}

export function useUser() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const lastIdentifiedId = useRef<string | null>(null)

  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: ({ signal }) => fetchMe(signal),
    staleTime: 30_000,
    retry: false
  })

  useEffect(() => {
    const user = query.data
    if (!user) return
    const previous = lastIdentifiedId.current
    if (user.id === previous) return

    /** identity switched mid-session (logout → login as someone else, or
     *  an oauth callback that swapped users without an explicit logout).
     *  wipe per-user caches so stale ['setup'] / ['supplier-me'] / etc.
     *  from the previous user can't survive into the new one. ['me'] gets
     *  cleared too but the in-flight query rehydrates it immediately.
     */
    if (previous !== null) queryClient.clear()
    identify(user.id)
    setUser({ id: user.id, email: user.email })
    lastIdentifiedId.current = user.id
  }, [query.data, queryClient])

  async function logout() {
    reset()
    clearUser()
    lastIdentifiedId.current = null
    await authClient.signOut()
    // the socket stays authenticated as this user until it's closed
    disconnectSocket()
    queryClient.clear()
    void navigate('/login', { replace: true })
  }

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    refetch: query.refetch,
    logout
  }
}
